import { z } from "zod";

export const temporaryIsrcPattern = /^LIS-TEMP-[0-9A-F]{32}$/;
export const isrcPattern = /^[A-Z]{2}[A-Z0-9]{3}[0-9]{7}$/;

export const normalizeIsrc = (value: string): string =>
  value.trim().replaceAll("-", "").replaceAll(" ", "").toUpperCase();

export const isValidIsrc = (value: string): boolean => isrcPattern.test(value);

export const isValidTemporaryIsrc = (value: string): boolean =>
  temporaryIsrcPattern.test(value);

export const setListEntrySchema = z.object({
  startSeconds: z
    .number()
    .describe("Where the track starts in the set, in seconds from its start."),
  title: z.string().describe("The track's title."),
  artist: z.string().describe("The track's artist."),
});

export const setListSchema = z.object({
  entries: z.array(setListEntrySchema),
});

export const songSchema = z.object({
  id: z.string(),
  title: z.string(),
  duration: z.number(),
  urlOnline: z.string().nullable(),
  private: z.boolean(),
  accessId: z.string().optional(),
  genres: z.array(z.object({ id: z.string(), name: z.string() })),
  artists: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      role: z.string(),
      image: z.string().nullable(),
    }),
  ),
  albums: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      image: z.string(),
      trackNumber: z.number(),
    }),
  ),
  // With on-demand publish (docs/contexts/audio-publish/reference/publishing.md),
  // "unpublished" is no
  // longer a client-facing state -- collapsed back to the original two
  // values. A VALID catalog song (has source audio) is always "internal",
  // whether or not the worker has transcoded it yet -- see `urlOnline`'s
  // continuity property below. Older app builds (below
  // `ON_DEMAND_PLAYBACK_MIN_APP_VERSION`, `mapSongStatusForAppVersion.ts`)
  // don't know how to poll/retry a not-yet-materialized `urlOnline`, so the
  // mapper downgrades those specific rows to `"external"` + `urlOnline: null`
  // for them only -- new clients always see the real `"internal"` + url.
  //
  // `urlOnline` semantics depend on `status`:
  //  - `"internal"`: the deterministic on-demand-publish URL
  //    (`apps/worker-publish` transcodes to this EXACT path -- the
  //    "continuity property": it's the SAME string before and after
  //    materialization, only whether a file exists at that URL changes). May
  //    404 until materialized -- a publish-intent fires server-side on read
  //    (album-open/song-detail), and the client itself rescues a still-404ing
  //    tap via `catalog.songs.requestPublish` -- so consumers must
  //    retry/backoff on this URL rather than assume it's immediately
  //    playable.
  //  - `"external"`: empty string/null (no Lissen-hosted audio -- either no
  //    source audio at all, or an old-client downgrade of the above).
  status: z.enum(["internal", "external"]),
  // Display-only catalog metadata on artist profiles. Legacy clients still
  // block it through status=external; current clients explain unavailability.
  unavailable: z.literal(true).optional(),
  advisory: z.string().optional().nullable(),
  isSetList: z.boolean().default(false),
  setList: setListSchema.optional(),
});

export type Song = z.infer<typeof songSchema>;

export const searchSongSchema = songSchema.extend({
  type: z.literal("song"),
});

export type SearchSong = z.infer<typeof searchSongSchema>;

/**
 * Wire-shape for `catalog.songs.listRecommendedSongs`: an array of
 * full songs. Used by the durable-cache layer (Phase 2.5 migration of
 * `useRecommendedSongs`). Empty arrays are legitimate (a brand-new user
 * has no recs yet), so this schema does NOT enforce non-emptiness — see
 * the consumer hook for the `validate` discussion.
 */
export const recommendedSongsListSchema = z.array(songSchema);

export type RecommendedSongsList = z.infer<typeof recommendedSongsListSchema>;

/**
 * Wire-shape for `catalog.songs.getCreditsById`. Used by the durable-cache
 * layer so the song credits panel renders from cache when the device is
 * offline. Server is the source of truth for credit composition; the
 * client never derives this locally, so a simple cache-then-refetch is
 * sufficient.
 */
export const songCreditsWireSchema = z.object({
  artists: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      image: z.string().nullable(),
      role: z.enum(["MainArtist", "FeaturedArtist"]),
      // False when the profile route would answer NOT_FOUND for this artist
      // (missing or operator-hidden identity, independent of song availability).
      profileAvailable: z.boolean(),
    }),
  ),
});

export type SongCredits = z.infer<typeof songCreditsWireSchema>;
